import { useEffect, useRef } from 'react';
import {
  NavigationType,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import browserHistoryEntries from '~/services/BrowserHistoryEntries';
import { getScrollContainer } from '~/utils/resetScroll';

/** Bound on remembered entries, so a long session cannot grow the map forever. */
const MAX_ENTRIES = 50;

/**
 * Shortest gap between two recordings.
 *
 * Throttled rather than deferred to the end of the scroll: waiting for the
 * reader to settle loses the position entirely when they flick and tap a
 * product straight away, and the recording that eventually fires then lands on
 * the product page, filed under it. Ten times a second costs little and is
 * never more than this far behind.
 */
const RECORD_INTERVAL_MS = 100;

/**
 * How long the return lock may stay closed without a restore clearing it.
 *
 * The lock is opened by `popstate` and normally closed by the restore that
 * follows. A pop that never reaches one — two of them in quick succession,
 * where the second is cleared by the first restore's teardown — would otherwise
 * leave recording shut for good.
 */
const RETURN_GUARD_MS = 2000;

/** How long a restore waits for its anchor to come back into the DOM. */
const ANCHOR_TIMEOUT_MS = 3000;

/**
 * How long after landing the position is checked once more.
 *
 * The mobile layout writes `--header-height` onto the body from an effect, and
 * the content is laid out against it, so everything can shift by the height of
 * a header a frame after the anchor was already in place.
 */
const RESETTLE_MS = 400;

/**
 * Where the reader was, described by what they were looking at.
 *
 * A pixel offset alone does not survive this app: the header height arrives
 * after the return and moves everything below it, images resolve and change
 * the height of the rows above. An element keeps its identity through all of
 * that, so the offset is only kept as a fallback for when the element is gone.
 */
interface RememberedPosition {
  /** Offset of the scroller, used when the anchor cannot be found again. */
  top: number;
  /**
   * Path the position was measured on. A new navigation reuses the index of the
   * forward entries it drops, so the path is what tells a genuine return apart
   * from a stale offset left by a page that no longer sits at that index.
   */
  path: string;
  anchor: Anchor | null;
}

interface Anchor {
  /** Selector matching the element the reader had at the top of the screen. */
  selector: string;
  /** Which match it was — one product renders more than one link to itself. */
  nth: number;
  /** Where the element sat relative to the top of the viewport. */
  offset: number;
}

const positions = new Map<number, RememberedPosition>();

/**
 * The last position seen on each path, whatever entry it was seen under.
 *
 * The entry index alone loses the position whenever the app pushes a new entry
 * for the same route — which it does for every modal it opens through the query
 * string, age confirmation and address among them. The reader then comes back
 * to an entry that never had a position of its own, having in fact never left
 * the page. This is what that case falls back to.
 */
const lastByPath = new Map<string, RememberedPosition>();

const bound = <K, V>(map: Map<K, V>) => {
  if (map.size > MAX_ENTRIES) {
    const oldest = map.keys().next().value;

    if (oldest !== undefined) {
      map.delete(oldest);
    }
  }
};

const remember = (index: number, position: RememberedPosition) => {
  positions.delete(index);
  positions.set(index, position);
  lastByPath.delete(position.path);
  lastByPath.set(position.path, position);

  bound(positions);
  bound(lastByPath);
};

interface Scroller {
  events: HTMLElement | Window;
  /** Top edge of the scrolling viewport, in client coordinates. */
  viewportTop: () => number;
  read: () => number;
  write: (top: number) => void;
}

/** The element that actually scrolls, wrapped so callers need not care which. */
const getScroller = (isMobile?: boolean): Scroller => {
  const container = getScrollContainer(isMobile);

  if (container) {
    return {
      events: container,
      viewportTop: () => container.getBoundingClientRect().top,
      read: () => container.scrollTop,
      write: (top) => {
        container.scrollTop = top;
      },
    };
  }

  return {
    events: window,
    viewportTop: () => 0,
    read: () => window.scrollY,
    write: (top) => window.scrollTo(0, top),
  };
};

/**
 * Describes the element at the top of the screen.
 *
 * Asked of the browser directly rather than measured across every card on the
 * page: hit-testing one point costs the same whether the listing holds fifty
 * products or five hundred.
 */
const readAnchor = (scroller: Scroller): Anchor | null => {
  const viewportTop = scroller.viewportTop();
  const found = document
    .elementFromPoint(Math.round(window.innerWidth / 2), viewportTop + 4)
    ?.closest('a[href]');

  const href = found?.getAttribute('href');

  if (!found || !href) {
    return null;
  }

  const selector = `a[href="${href.replace(/"/g, '\\"')}"]`;
  const matches = Array.from(document.querySelectorAll(selector));

  return {
    selector,
    nth: Math.max(matches.indexOf(found), 0),
    offset: Math.round(found.getBoundingClientRect().top - viewportTop),
  };
};

/**
 * Puts the anchor back where it was, if it is on the page.
 *
 * Scrolls by the difference rather than to an absolute offset, so it lands
 * correctly however much the content above has moved since.
 *
 * @returns Whether the anchor was found.
 */
const applyAnchor = (scroller: Scroller, anchor: Anchor): boolean => {
  const matches = document.querySelectorAll(anchor.selector);
  const element = matches[anchor.nth] ?? matches[0];

  if (!element) {
    return false;
  }

  const delta =
    element.getBoundingClientRect().top -
    scroller.viewportTop() -
    anchor.offset;

  if (Math.abs(delta) < 1) {
    return true;
  }

  scroller.write(scroller.read() + delta);

  return true;
};

/**
 * Puts the reader back where they were when they go back to a page.
 *
 * Browser scroll restoration is turned off here in favour of this: it restores
 * into a list that has not rendered yet — the same journey landed at 0, at 1500
 * and at 9278 of a 10490px page — and the scroll it fires on the way is
 * indistinguishable from the reader's own, so it overwrites the very position
 * being returned to.
 *
 * @param isMobile Whether the mobile layout is rendered.
 */
export const useScrollRestoration = ({ isMobile }: { isMobile?: boolean }) => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const index = browserHistoryEntries.getCurrentIndex();
  const pathRef = useRef(pathname);
  const indexRef = useRef(index);
  const isReturningRef = useRef(false);

  pathRef.current = pathname;
  indexRef.current = index;

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let guard: ReturnType<typeof setTimeout>;

    const markReturn = () => {
      isReturningRef.current = true;
      clearTimeout(guard);
      guard = setTimeout(() => {
        isReturningRef.current = false;
      }, RETURN_GUARD_MS);
    };

    window.addEventListener('popstate', markReturn);

    return () => {
      clearTimeout(guard);
      window.removeEventListener('popstate', markReturn);
    };
  }, []);

  useEffect(() => {
    const scroller = getScroller(isMobile);

    let trailing: ReturnType<typeof setTimeout>;
    let recordedAt = 0;

    const record = () => {
      recordedAt = performance.now();

      const current = browserHistoryEntries.getCurrentIndex();

      // The browser moves the history entry before React renders the route it
      // points at, so a scroll landing in that gap — the new page resetting
      // itself to the top, for one — would be filed under the new entry with
      // the old page's path. And nothing is recorded while returning: every
      // scroll then comes from the restore itself.
      if (
        current === null ||
        current !== indexRef.current ||
        isReturningRef.current
      ) {
        return;
      }

      remember(current, {
        top: scroller.read(),
        path: pathRef.current,
        anchor: readAnchor(scroller),
      });
    };

    const handleScroll = () => {
      const due = RECORD_INTERVAL_MS - (performance.now() - recordedAt);

      clearTimeout(trailing);

      if (due <= 0) {
        record();
        return;
      }

      // The scroll that ends a flick is the one worth having, and it arrives
      // inside the interval — so the skipped recording is rescheduled rather
      // than dropped.
      trailing = setTimeout(record, due);
    };

    scroller.events.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(trailing);
      scroller.events.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    if (index === null || navigationType !== NavigationType.Pop) {
      isReturningRef.current = false;
      return;
    }

    const scroller = getScroller(isMobile);
    const remembered = positions.get(index);
    const known =
      remembered?.path === pathname ? remembered : lastByPath.get(pathname);

    // Nothing remembered means nothing to restore, and the document simply
    // stays where the new route put it. The container is the exception: it
    // survives the route change carrying the offset of the page being left, so
    // an unknown position there still has to be written back to the top.
    if (!known) {
      isReturningRef.current = false;

      if (scroller.events !== window) {
        scroller.write(0);
      }

      return;
    }

    let resettle: ReturnType<typeof setTimeout>;
    let deadline: ReturnType<typeof setTimeout>;

    const finish = () => {
      observer.disconnect();
      clearTimeout(deadline);
      // Held open for one more beat: the layout is still settling — the header
      // height lands from an effect, images resolve — and each of those moves
      // the anchor after it was put in place.
      resettle = setTimeout(() => {
        if (known.anchor) {
          applyAnchor(scroller, known.anchor);
        }

        isReturningRef.current = false;
      }, RESETTLE_MS);
    };

    const attempt = () => {
      if (!known.anchor) {
        scroller.write(known.top);
        finish();
        return;
      }

      if (applyAnchor(scroller, known.anchor)) {
        finish();
      }
    };

    // The list is rendered from its own chunk and arrives a frame or several
    // after the navigation lands, so the anchor is waited for rather than
    // looked up once. Watching the DOM costs nothing until it changes, unlike
    // re-measuring the page on every frame.
    const observer = new MutationObserver(attempt);

    observer.observe(document.body, { childList: true, subtree: true });
    deadline = setTimeout(() => {
      observer.disconnect();
      scroller.write(known.top);
      finish();
    }, ANCHOR_TIMEOUT_MS);

    attempt();

    return () => {
      observer.disconnect();
      clearTimeout(deadline);
      clearTimeout(resettle);
      isReturningRef.current = false;
    };
    // `navigationType` and `pathname` are deliberately absent: the entry index
    // is what identifies a restore, and both of the others change again while
    // the catalog settles its query string, which would abort the restore in
    // flight.
  }, [index, isMobile]);
};
